/**
 * Service Layer - Participant Service
 * Handles participant management and tracking
 * Updates State layer (store)
 */

import {
    PARTICIPANT_INACTIVE_THRESHOLD,
    PARTICIPANT_REMOVE_THRESHOLD,
    VIDEO_INACTIVE_THRESHOLD,
    AUDIO_INACTIVE_THRESHOLD,
    messageTypeEnum
} from '../core/constants/RtcEnums';

export class ParticipantService {
    constructor(store, socketManager, streamService) {
        this.store = store;
        this.socketManager = socketManager;
        this.streamService = streamService;
        this.activityCheckInterval = null;
    }

    /**
     * Start monitoring participant activity
     */
    startActivityMonitoring() {
        this.activityCheckInterval = setInterval(() => {
            this.checkParticipantActivity();
            this.checkVideoActivity();
            this.checkAudioActivity();
        }, VIDEO_INACTIVE_THRESHOLD);
    }

    /**
     * Stop monitoring participant activity
     */
    stopActivityMonitoring() {
        if (this.activityCheckInterval) {
            clearInterval(this.activityCheckInterval);
            this.activityCheckInterval = null;
        }
    }

    /**
     * Check participant activity (heartbeat-based)
     */
    checkParticipantActivity() {
        Object.entries(this.store.activeParticipantsTimes).forEach(([senderId, time]) => {
            // Mark as inactive if no heartbeat for 10 seconds
            if (time < Date.now() - PARTICIPANT_INACTIVE_THRESHOLD) {
                this.store.activeParticipants[senderId] = false;
            }

            // Remove participant if no heartbeat for 40 seconds
            if (time < Date.now() - PARTICIPANT_REMOVE_THRESHOLD) {
                this.store.participantsMap.delete(senderId);
            }
        });
    }

    /**
     * Check video activity
     */
    checkVideoActivity() {
        Object.entries(this.store.activeVideosTimes).forEach(([senderId, time]) => {
            if (time < Date.now() - VIDEO_INACTIVE_THRESHOLD && this.store.activeVideos[senderId]) {
                this.store.activeVideos[senderId] = false;

                // Show black frame
                const participant = this.store.participantsMap.get(senderId);
                if (participant) {
                    const blackFrame = this.createBlackFrame();
                    this.streamService.renderFrame(participant.slot, blackFrame, senderId);
                }
            }
        });
    }

    /**
     * Check audio activity
     */
    checkAudioActivity() {
        Object.entries(this.store.activeAudiosTimes).forEach(([connectionId, time]) => {
            if (time < Date.now() - AUDIO_INACTIVE_THRESHOLD) {
                this.store.activeAudios[connectionId] = false;
            }
        });
    }

    /**
     * Handle participant heartbeat
     */
    handleHeartbeat(data) {
        const parsedData = JSON.parse(data);
        const participantId = parsedData.SenderSocketId;

        if (participantId === this.store.deviceId) {
            return;
        }

        this.store.activeParticipantsTimes[participantId] = Date.now();
        this.store.activeParticipants[participantId] = true;

        // Join video channel if new participant
        if (!this.store.participantsMap.has(participantId)) {
            const videoChannel = `dynamic${participantId}High`;
            this.socketManager.emit("JoinRoom", videoChannel);

            // Request keyframe after a delay
            setTimeout(() => {
                this.streamService.requestKeyFrame(participantId);
                this.broadcastMicState();
            }, 500);

            const slot = this.getFirstAvailableSlot();
            const decoder = this.streamService.createParticipantDecoder(slot, participantId);

            this.store.participantsMap.set(participantId, {
                slot,
                decoder
            });
            this.store.participantsMicStates.set(participantId, parsedData.micOn);
        }
    }

    /**
     * Handle participant left
     */
    handleParticipantLeft(stringifiedData) {
        const userLeftMessage = JSON.parse(stringifiedData);
        const senderId = userLeftMessage.SenderSocketId;
        this.store.participantsMap.delete(senderId);
        delete this.store.activeParticipants[senderId];
        delete this.store.activeParticipantsTimes[senderId];
        delete this.store.activeVideos[senderId];
        delete this.store.activeVideosTimes[senderId];
        delete this.store.activeAudios[senderId];
        delete this.store.activeAudiosTimes[senderId];
    }

    /**
     * Handle camera feed metadata
     */
    handleCameraFeed(messageData) {
        const senderId = messageData.SessionID;
        this.store.activeVideosTimes[senderId] = Date.now();
        this.store.activeVideos[senderId] = true;
        this.store.latestFramesObj[senderId] = messageData;

        // Store frame info for frozen frames
        if (!this.isBigVideoSelf() && this.store.biggerWindowConnectionId === senderId) {
            this.store.framesMap.set(messageData.FrameId, messageData);
        }
    }

    /**
     * Handle mic state change
     */
    handleMicState(messageData) {
        const participantId = messageData.SessionID || messageData.DeviceGUID;

        if (participantId === this.store.deviceId) {
            return;
        }

        const micState = messageData.hasOwnProperty('isMuted') ? !messageData.isMuted : messageData.micOn;
        this.store.participantsMicStates.set(participantId, micState);
    }

    /**
     * Broadcast own mic state
     */
    broadcastMicState() {
        const basePayload = {
            isMuted: !this.store.micOn,
            micOn: this.store.micOn,
            DeviceGUID: this.store.deviceId,
            SessionID: this.store.deviceId
        };

        this.socketManager.sendRoomMessage(
                this.store.roomId,
                JSON.stringify({ ...basePayload, MessageId: messageTypeEnum.CAMERA_FEED }),
                this.store.deviceId
        );

         this.socketManager.sendRoomMessage(
            this.store.roomId,
            JSON.stringify({ ...basePayload, MessageId: messageTypeEnum.MIC_STATE }),
            this.store.deviceId
        );
    }


    /**
     * Get first available slot for participant
     */
    getFirstAvailableSlot() {
        const usedSlots = new Set();
        [...this.store.participantsMap.values()].forEach(participant => usedSlots.add(participant.slot));

        if (!usedSlots.size) {
            return 0;
        }

        const sortedSlotsArr = [...usedSlots.keys()].sort();
        return sortedSlotsArr[sortedSlotsArr.length - 1] + 1;
    }

    /**
     * Create a black frame image
     */
    createBlackFrame() {
        const img = new Image();
        img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAUEBAAAACwAAAAAAQABAAACAkQBADs=';
        return img;
    }

    /**
     * Check if big video is self
     */
    isBigVideoSelf() {
        return this.store.biggerWindowConnectionId === this.store.deviceId;
    }

    /**
     * Get participant display name
     */
    getParticipantName(participantId) {
        const frameInfo = this.store.latestFramesObj[participantId];
        const user = this.store.participants[frameInfo?.DeviceGUID];
        return user?.Name || user?.GuestName || 'Guest';
    }

    /**
     * Cleanup
     */
    cleanup() {
        this.stopActivityMonitoring();
        this.store.participantsMap.clear();
        this.store.activeParticipants = {};
        this.store.activeParticipantsTimes = {};
        this.store.activeVideos = {};
        this.store.activeVideosTimes = {};
        this.store.activeAudios = {};
        this.store.activeAudiosTimes = {};
    }
}
